/**
 * 用户偏好管理服务
 * 管理用户的语言、考试类型和模式选择等偏好设置
 */

import { getAllAvailableExams, getExam, getLanguages, getExamsByLanguage } from '@examflow/core';
import { isLevelSupportedForExam } from '@examflow/exams';

const LANGUAGE_KEY = 'examflow_language';
const EXAM_TYPE_KEY = 'examflow_exam_type';
const QUIZ_MODE_KEY = 'examflow_quiz_mode';
const DIFFICULTY_KEY = 'examflow_difficulty';
const SETTINGS_KEY = 'examflow_settings';

// ========== 语言选择 ==========

/**
 * 获取用户选择的语言
 */
export function getLanguage(): string | null {
  return localStorage.getItem(LANGUAGE_KEY);
}

/**
 * 设置用户选择的语言
 */
export function setLanguage(languageId: string): void {
  localStorage.setItem(LANGUAGE_KEY, languageId);
}

/**
 * 检查用户是否已选择语言
 */
export function hasSelectedLanguage(): boolean {
  return !!localStorage.getItem(LANGUAGE_KEY);
}

/**
 * 清除语言选择
 */
export function clearLanguage(): void {
  localStorage.removeItem(LANGUAGE_KEY);
}

// ========== 考试类型选择 ==========

/**
 * 获取用户选择的考试类型
 */
export function getExamType(): string | null {
  return localStorage.getItem(EXAM_TYPE_KEY);
}

/**
 * 获取当前激活的备考考试。
 * 复用历史考试类型 key，避免破坏既有路由守卫和本地题库逻辑。
 */
export function getActiveExamType(): string | null {
  return getExamType();
}

/**
 * 设置用户选择的考试类型
 */
export function setExamType(examType: string): void {
  localStorage.setItem(EXAM_TYPE_KEY, examType);
}

/**
 * 设置当前激活的备考考试。
 */
export function setActiveExamType(examType: string): void {
  setExamType(examType);
}

/**
 * Persist language + exam from a learning-route URL scope.
 * Idempotent: skips writes when values already match.
 */
export function syncLearningRoutePreferences(scope: {
  languageId: string | null;
  examType: string | null;
}): boolean {
  const languageId = scope.languageId?.trim() || null;
  const examType = scope.examType?.trim() || null;
  if (!languageId || !examType) return false;

  let changed = false;
  if (getLanguage() !== languageId) {
    setLanguage(languageId);
    changed = true;
  }
  if (getActiveExamType() !== examType) {
    setActiveExamType(examType);
    changed = true;
  }
  return changed;
}

/**
 * Login/register redirect target that preserves the learner's last exam intent.
 */
export function buildPreferredWorkspaceHref(fallbackExamType?: string | null): string {
  const languageId = getLanguage();
  const examType = getActiveExamType();
  if (languageId && examType) {
    return `/workspace?languageId=${encodeURIComponent(languageId)}&examType=${encodeURIComponent(examType)}`;
  }

  const fallback = fallbackExamType?.trim();
  if (fallback) {
    return buildWorkspaceHrefForExam(fallback);
  }

  return '/workspace';
}

/** Always deep-link a specific Exam Space (ignores last-used preference). */
export function buildWorkspaceHrefForExam(examType: string): string {
  const normalized = examType.trim().toLowerCase();
  if (!normalized) return '/workspace';

  const examInfo = getExam(normalized);
  if (examInfo?.language.id) {
    return `/workspace?languageId=${encodeURIComponent(examInfo.language.id)}&examType=${encodeURIComponent(normalized)}`;
  }

  return `/workspace?examType=${encodeURIComponent(normalized)}`;
}

/**
 * 检查用户是否已选择考试类型
 */
export function hasSelectedExamType(): boolean {
  return !!localStorage.getItem(EXAM_TYPE_KEY);
}

/**
 * 清除考试类型选择
 */
export function clearExamType(): void {
  localStorage.removeItem(EXAM_TYPE_KEY);
}

/**
 * 获取当前语言下的可用考试类型列表
 */
export function getAvailableExamsForLanguage(): ReturnType<typeof getAllAvailableExams> {
  const languageId = getLanguage();
  if (!languageId) {
    return [];
  }
  return getExamsByLanguage(languageId);
}

/**
 * 获取可用的考试类型列表（基于选择的语言过滤）
 */
export function getExamTypes() {
  const languageId = getLanguage();
  if (!languageId) {
    return [];
  }
  return getExamsByLanguage(languageId).map((exam) => ({
    id: exam.id,
    name: exam.name,
    fullName: exam.fullName,
    description: exam.description,
    color: exam.color,
    icon: exam.icon || '',
    available: exam.available,
  }));
}

// ========== 模式选择 ==========

/**
 * 获取用户选择的练习模式
 */
export function getQuizMode(): 'practice' | 'exam' | null {
  const mode = localStorage.getItem(QUIZ_MODE_KEY);
  if (mode === 'practice' || mode === 'exam') {
    return mode;
  }
  return null;
}

/**
 * 设置用户选择的练习模式
 */
export function setQuizMode(mode: 'practice' | 'exam'): void {
  localStorage.setItem(QUIZ_MODE_KEY, mode);
}

/**
 * 检查用户是否已选择模式
 */
export function hasSelectedQuizMode(): boolean {
  return !!localStorage.getItem(QUIZ_MODE_KEY);
}

/**
 * 清除模式选择
 */
export function clearQuizMode(): void {
  localStorage.removeItem(QUIZ_MODE_KEY);
}

// ========== 难度选择 ==========

export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type DifficultyLevel = (typeof CEFR_LEVELS)[number];

const LEGACY_DIFFICULTY_KEY = DIFFICULTY_KEY;
const TRAINING_LEVEL_BY_EXAM_KEY = 'examflow_training_level_by_exam';

/**
 * @deprecated Prefer getTrainingDifficulty(examType). Kept for one-time migration.
 * 获取用户选择的难度（全局 CEFR-only；不再作为 Quiz 组卷默认源）
 */
export function getDifficulty(): DifficultyLevel | null {
  const value = localStorage.getItem(LEGACY_DIFFICULTY_KEY) as DifficultyLevel | null;
  if (CEFR_LEVELS.includes(value as DifficultyLevel)) {
    return value;
  }
  return null;
}

/**
 * @deprecated Prefer setTrainingDifficulty(examType, level).
 */
export function setDifficulty(level: DifficultyLevel): void {
  localStorage.setItem(LEGACY_DIFFICULTY_KEY, level);
}

/**
 * 检查是否已选择难度
 */
export function hasSelectedDifficulty(): boolean {
  return !!getDifficulty();
}

/**
 * 清除难度选择
 */
export function clearDifficulty(): void {
  localStorage.removeItem(LEGACY_DIFFICULTY_KEY);
}

function readTrainingLevelByExam(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TRAINING_LEVEL_BY_EXAM_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return {};
    const result: Record<string, string> = {};
    for (const [examType, level] of Object.entries(parsed)) {
      if (typeof level === 'string' && level.trim()) {
        result[examType.toLowerCase()] = level.trim();
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeTrainingLevelByExam(map: Record<string, string>): void {
  localStorage.setItem(TRAINING_LEVEL_BY_EXAM_KEY, JSON.stringify(map));
}

/**
 * Per-exam training entry prefill only — never use as silent Quiz paper filter.
 * Returns '' when unset / 不限定.
 */
export function getTrainingDifficulty(examType: string): string {
  const key = examType.trim().toLowerCase();
  if (!key) return '';
  const stored = readTrainingLevelByExam()[key];
  if (stored && isLevelSupportedForExam(key, stored)) return stored;

  // One-time bridge from legacy global CEFR key when it fits the exam scale.
  const legacy = getDifficulty();
  if (legacy && isLevelSupportedForExam(key, legacy)) {
    return legacy;
  }
  return '';
}

/**
 * Persist training entry difficulty preference for one Exam.
 * Pass null or '' to clear (不限定).
 */
export function setTrainingDifficulty(examType: string, level: string | null): void {
  const key = examType.trim().toLowerCase();
  if (!key) return;
  const map = readTrainingLevelByExam();
  const trimmed = level?.trim() ?? '';
  if (!trimmed) {
    delete map[key];
  } else {
    map[key] = trimmed;
  }
  writeTrainingLevelByExam(map);
}

// ========== 语言列表 ==========

/**
 * 获取所有可用语言列表
 */
export function getAvailableLanguages() {
  return getLanguages();
}

// ========== 设置页持久化（主题、界面语言、练习选项） ==========

export interface UiSettings {
  theme: 'light' | 'dark' | 'system';
  language: string;
  showExplanation: boolean;
  soundEnabled: boolean;
  autoNext: boolean;
}

const DEFAULT_UI_SETTINGS: UiSettings = {
  theme: 'system',
  language: 'zh-CN',
  showExplanation: true,
  soundEnabled: true,
  autoNext: false,
};

/**
 * 获取持久化的设置（外观与练习选项）
 */
export function getUiSettings(): UiSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_UI_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<UiSettings>;
    return { ...DEFAULT_UI_SETTINGS, ...parsed };
  } catch {
    return { ...DEFAULT_UI_SETTINGS };
  }
}

/**
 * 持久化设置到本地存储
 */
export function setUiSettings(settings: Partial<UiSettings>): void {
  const current = getUiSettings();
  const next = { ...current, ...settings };
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
}
